import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getRole } from "../../../../../../../apis/user";
import { useParams } from "react-router-dom";
import {
  XIcon,
  UserAddIcon,
  UserIcon,
  MailIcon,
  LockClosedIcon,
  ShieldCheckIcon,
  OfficeBuildingIcon,
} from "@heroicons/react/outline";

const CreatePropertyUserModal = ({
  isOpen,
  onClose,
  fetchUsers,
  resortId: propResortId,
}) => {
  const [role, setRole] = useState(null);
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const { id: routeResortId } = useParams();
  const resortId = propResortId || routeResortId;

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    password_confirmation: "",
    role: "",
    resort_id: resortId || "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchRole = async () => {
    try {
      const response = await getRole();
      setRole(response.data);
    } catch (error) {
      console.log("Error fetching roles: ", error);
      return "";
    }
  };

  useEffect(() => {
    fetchRole();
  }, []);

  // Supervisors may create property managers; they cannot grant supervisor
  // privileges to another account.
  const defaultRoleValue = React.useMemo(() => {
    if (!Array.isArray(role)) return "";
    const found = role.find(
      (r) =>
        (r.name && r.name.toLowerCase() === "resort-manager") ||
        (r.role_name && r.role_name.toLowerCase() === "resort-manager") ||
        (r.description && r.description.toLowerCase() === "resort manager")
    );
    return found ? found.name || found.role_name || "" : "";
  }, [role]);

  const defaultRoleObj = React.useMemo(() => {
    if (!Array.isArray(role)) return null;
    return role.find(
      (r) =>
        (r.name && r.name.toLowerCase() === "resort-manager") ||
        (r.role_name && r.role_name.toLowerCase() === "resort-manager") ||
        (r.description && r.description.toLowerCase() === "resort manager")
    );
  }, [role]);

  // Lock the modal to the only role a property supervisor may create.
  useEffect(() => {
    if (defaultRoleValue) {
      setFormData((prev) => ({ ...prev, role: prev.role || defaultRoleValue }));
    }
  }, [defaultRoleValue]);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, resort_id: resortId || prev.resort_id }));
  }, [resortId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!resortId) {
      toast.error("A property must be selected before creating a property user.");
      return;
    }
    setIsSubmitting(true);
    try {
      const accessToken = localStorage.getItem("accessToken");

      const response = await fetch(`${baseUrl}/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const payload = await response.json().catch(() => null);
        if (payload?.data?.welcome_email_sent) {
          toast.success(`Property user created. Welcome email sent to ${payload.data.welcome_email_recipient}.`);
        } else {
          toast.success("Property user created. The account is ready for sign-in.");
        }
        setFormData({
          first_name: "",
          last_name: "",
          email: "",
          password: "",
          password_confirmation: "",
          role: defaultRoleValue || "",
          resort_id: resortId || "",
        });
        onClose();
        if (fetchUsers) fetchUsers();
      } else {
        const err = await response.json().catch(() => null);
        toast.error(err?.message || "Failed to create user");
        console.error("Failed to create user", err);
      }
    } catch (error) {
      toast.error("Error occurred while creating user");
      console.error("Error occurred while creating user:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="flex min-h-screen items-center justify-center p-4">
          <div className="relative w-full max-w-2xl transform transition-all">
            <div className="modal-container">
              {/* Header */}
              <div className="modal-header">
                <div className="flex items-center space-x-3">
                  <div className="modal-icon">
                    <UserAddIcon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="modal-title">Create Property User</h3>
                    <p className="modal-subtitle">
                      Add a new user to this property
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="modal-close-btn"
                  type="button"
                >
                  <XIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Property ID Info */}
                    <div className="form-group md:col-span-2">
                      <div className="info-banner">
                        <OfficeBuildingIcon className="w-5 h-5" />
                        <span>
                          Creating user for Property ID:{" "}
                          <strong>{resortId}</strong>
                        </span>
                      </div>
                    </div>

                    {/* First Name */}
                    <div className="form-group">
                      <label htmlFor="first_name" className="form-label">
                        <UserIcon className="form-label-icon" />
                        First Name *
                      </label>
                      <input
                        type="text"
                        id="first_name"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="e.g., John"
                        required
                      />
                    </div>

                    {/* Last Name */}
                    <div className="form-group">
                      <label htmlFor="last_name" className="form-label">
                        <UserIcon className="form-label-icon" />
                        Last Name *
                      </label>
                      <input
                        type="text"
                        id="last_name"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="e.g., Doe"
                        required
                      />
                    </div>

                    {/* Email */}
                    <div className="form-group md:col-span-2">
                      <label htmlFor="email" className="form-label">
                        <MailIcon className="form-label-icon" />
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="e.g., john.doe@example.com"
                        required
                      />
                    </div>

                    {/* Password */}
                    <div className="form-group">
                      <label htmlFor="password" className="form-label">
                        <LockClosedIcon className="form-label-icon" />
                        Password *
                      </label>
                      <input
                        type="password"
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="At least 12 characters"
                        required
                      />
                    </div>

                    {/* Confirm Password */}
                    <div className="form-group">
                      <label
                        htmlFor="password_confirmation"
                        className="form-label"
                      >
                        <LockClosedIcon className="form-label-icon" />
                        Confirm Password *
                      </label>
                      <input
                        type="password"
                        id="password_confirmation"
                        name="password_confirmation"
                        value={formData.password_confirmation}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="Re-enter password"
                        required
                      />
                    </div>

                    {/* Role */}
                    <div className="form-group md:col-span-2">
                      <label htmlFor="role" className="form-label">
                        <ShieldCheckIcon className="form-label-icon" />
                        User Role *
                      </label>
                      <select
                        id="role"
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                        className="form-select"
                        required
                        disabled={!!defaultRoleObj}
                      >
                        {defaultRoleObj ? (
                          <>
                            <option
                              value={
                                defaultRoleObj.name || defaultRoleObj.role_name
                              }
                            >
                              {defaultRoleObj.description ||
                                defaultRoleObj.name ||
                                defaultRoleObj.role_name}
                            </option>
                          </>
                        ) : (
                          <>
                            <option value="">Select a role</option>
                            {role?.map((r) => (
                              <option key={r.role_id} value={r.name}>
                                {r.description || r.name}
                              </option>
                            ))}
                          </>
                        )}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="modal-footer">
                  <button
                    type="button"
                    onClick={onClose}
                    className="btn-secondary"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <svg
                          className="animate-spin h-5 w-5 mr-2"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Creating...
                      </>
                    ) : (
                      "Create User"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Styles */}
      <style jsx>{`
        .modal-container {
          background: white;
          border-radius: 1rem;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          overflow: hidden;
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.5rem;
          background: linear-gradient(135deg, #ec4899, #be185d);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .modal-icon {
          width: 3rem;
          height: 3rem;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .modal-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: white;
          margin: 0;
        }

        .modal-subtitle {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.9);
          margin: 0.25rem 0 0 0;
        }

        .modal-close-btn {
          padding: 0.5rem;
          background: rgba(255, 255, 255, 0.2);
          border: none;
          border-radius: 0.5rem;
          color: white;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .modal-close-btn:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: scale(1.1);
        }

        .modal-body {
          padding: 2rem;
          max-height: 60vh;
          overflow-y: auto;
        }

        .modal-body::-webkit-scrollbar {
          width: 8px;
        }

        .modal-body::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }

        .modal-body::-webkit-scrollbar-thumb {
          background: #ec4899;
          border-radius: 4px;
        }

        .modal-body::-webkit-scrollbar-thumb:hover {
          background: #be185d;
        }

        .info-banner {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem;
          background: linear-gradient(135deg, #fce7f3, #fbcfe8);
          border: 1px solid #f9a8d4;
          border-radius: 0.75rem;
          color: #831843;
          font-size: 0.875rem;
        }

        .form-group {
          margin-bottom: 0;
        }

        .form-label {
          display: flex;
          align-items: center;
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 0.5rem;
        }

        .form-label-icon {
          width: 1rem;
          height: 1rem;
          margin-right: 0.5rem;
          color: #ec4899;
        }

        .form-input,
        .form-select {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-size: 0.875rem;
          transition: all 0.2s ease;
          background: #f9fafb;
        }

        .form-input:focus,
        .form-select:focus {
          outline: none;
          border-color: #ec4899;
          background: white;
          box-shadow: 0 0 0 3px rgba(236, 72, 153, 0.1);
        }

        .form-select {
          cursor: pointer;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1.5rem;
          background: #f9fafb;
          border-top: 1px solid #e5e7eb;
        }

        .btn-secondary {
          padding: 0.75rem 1.5rem;
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-weight: 600;
          color: #374151;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #f3f4f6;
          border-color: #d1d5db;
        }

        .btn-secondary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-primary {
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, #ec4899, #be185d);
          border: none;
          border-radius: 0.75rem;
          font-weight: 600;
          color: white;
          cursor: pointer;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #db2777, #9f1239);
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(236, 72, 153, 0.4);
        }

        .btn-primary:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
      `}</style>
    </>
  );
};

export default CreatePropertyUserModal;
